import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { FiArrowLeft, FiBook, FiEdit3 } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import documentacoesService, {
  Documentacao,
} from "../../services/documentacoes";
import Skeleton from "../../components/ui/Skeleton";
import AuthenticatedImage from "../../components/ui/AuthenticatedImage";
import DocumentacaoEditModal from "../../components/documentacoes/DocumentacaoEditModal";
import { ImageModal } from "../../components/conversations/ImageModal";

function DocumentacaoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = !!user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [documentacao, setDocumentacao] = useState<Documentacao | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const loadDocumentacao = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await documentacoesService.getById(id);
      setDocumentacao(data);
    } catch (error) {
      console.error("Erro ao carregar documentação:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadDocumentacao();
    }
  }, [id, loadDocumentacao]);

  // Componente personalizado para renderizar imagens no markdown
  const MarkdownImage = ({ src, alt }: { src?: string; alt?: string }) => {
    if (!src) return null;

    const handleImageClick = (imageUrl: string) => {
      setSelectedImage(imageUrl);
    };

    // Se for uma URL completa, usa diretamente
    if (src.startsWith("http://") || src.startsWith("https://")) {
      return (
        <img
          src={src}
          alt={alt || ""}
          onClick={() => handleImageClick(src)}
          className="w-full rounded-lg my-4 shadow-md max-h-[400px] object-contain cursor-pointer hover:opacity-90 transition-opacity"
        />
      );
    }

    // Se for um caminho relativo, usa AuthenticatedImage com wrapper clicável
    return (
      <ClickableAuthenticatedImage
        fileName={src}
        alt={alt || ""}
        onImageClick={handleImageClick}
        className="w-full rounded-lg my-4 shadow-md max-h-[400px] object-contain"
      />
    );
  };

  // Componente wrapper para AuthenticatedImage clicável
  const ClickableAuthenticatedImage = ({
    fileName,
    alt,
    onImageClick,
    className,
  }: {
    fileName: string;
    alt: string;
    onImageClick: (url: string) => void;
    className?: string;
  }) => {
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // Tenta encontrar a imagem dentro do container clicado
      const img = e.currentTarget.querySelector("img");
      if (img?.src) {
        onImageClick(img.src);
      } else if (
        fileName.startsWith("http://") ||
        fileName.startsWith("https://")
      ) {
        onImageClick(fileName);
      } else {
        // Para caminhos relativos, constrói a URL da API
        const baseUrl = "https://api.appeasyfit.com.br";
        onImageClick(`${baseUrl}/images/download-public/${fileName}`);
      }
    };

    return (
      <div
        onClick={handleClick}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
          }
        }}
      >
        <AuthenticatedImage
          fileName={fileName}
          alt={alt}
          className={`${className} hover:opacity-90 transition-opacity`}
        />
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 py-4 sm:py-6 md:px-6 2xl:px-10">
      {/* Header Premium */}
      <div className="mb-4 sm:mb-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-purple-600 to-purple-700 p-4 sm:p-6 shadow-lg">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>

          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 sm:gap-4">
              <button
                onClick={() => navigate("/documentacoes")}
                className="rounded-xl bg-white/20 p-2.5 sm:p-3 backdrop-blur-sm hover:bg-white/30 transition-colors"
              >
                <FiArrowLeft className="text-white" size={24} />
              </button>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  {loading
                    ? "Carregando..."
                    : documentacao?.title || "Documentação"}
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-purple-100">
                  Manual do Sistema
                </p>
              </div>
            </div>
            {isAdmin && documentacao && (
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-105 active:scale-95 touch-manipulation"
              >
                <FiEdit3 size={18} />
                <span className="hidden sm:inline">Editar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card Principal */}
      <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden">
        {loading ? (
          <div className="p-6">
            <Skeleton className="mb-4 h-8 w-3/4" />
            <Skeleton className="mb-2 h-4 w-full" />
            <Skeleton className="mb-2 h-4 w-full" />
            <Skeleton className="mb-2 h-4 w-5/6" />
            <Skeleton className="mt-4 h-64 w-full" />
          </div>
        ) : documentacao ? (
          <div className="p-4 sm:p-6 md:p-8">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  img: ({ src, alt }) => <MarkdownImage src={src} alt={alt} />,
                  h1: ({ children }) => (
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 mt-6">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 mt-5">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-base sm:text-lg">{children}</li>
                  ),
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-purple-600 dark:text-purple-400">
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm font-mono text-gray-800 dark:text-gray-200 mb-4">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto mb-4">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-600 dark:text-gray-400 my-4">
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 dark:text-purple-400 hover:underline"
                    >
                      {children}
                    </a>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-4">
                      <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700">
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border border-gray-300 dark:border-gray-700 px-4 py-2 bg-gray-100 dark:bg-gray-800 font-semibold text-left">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">
                      {children}
                    </td>
                  ),
                }}
              >
                {documentacao.text}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 sm:py-16">
            <div className="mb-4 sm:mb-6 rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 p-4 sm:p-6 dark:from-purple-900/20 dark:to-purple-800/20">
              <FiBook
                size={40}
                className="text-purple-600 dark:text-purple-400 sm:w-12 sm:h-12"
              />
            </div>
            <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              Documentação não encontrada
            </p>
            <button
              onClick={() => navigate("/documentacoes")}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:scale-105 active:scale-95 touch-manipulation"
            >
              <FiArrowLeft size={18} />
              Voltar para lista
            </button>
          </div>
        )}
      </div>

      {/* Modal de Edição */}
      {isAdmin && (
        <DocumentacaoEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          documentacao={documentacao}
          onSuccess={() => {
            loadDocumentacao();
            setIsEditModalOpen(false);
          }}
        />
      )}

      {/* Modal de Imagem em Tela Cheia */}
      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage}
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          alt={documentacao?.title || "Imagem da documentação"}
        />
      )}
    </div>
  );
}

export default DocumentacaoDetail;
